import { useMemo, useState } from "react";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import { mediaList, typeLabel, type MediaType } from "@/mocks/media";

const PAGE_SIZE = 12;
const tabs: { key: "all" | MediaType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

export default function LatestMediaList() {
  const [tab, setTab] = useState<"all" | MediaType>("all");
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => [...mediaList].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
    []
  );

  const filtered = useMemo(
    () => (tab === "all" ? sorted : sorted.filter((item) => item.type === tab)),
    [tab, sorted]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeTab = (next: "all" | MediaType) => {
    setTab(next);
    setPage(1);
  };

  const changePage = (next: number) => {
    setPage(next);
    const anchor = document.getElementById("latest-list");
    if (anchor) anchor.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="latest-list" aria-label="最新影视列表" className="w-full min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl md:text-[22px] font-bold text-foreground-950 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            最新影视
          </h2>
          <p className="text-sm text-foreground-500 mt-1.5">
            按更新时间排序，共 <span className="text-primary-600 font-medium">{filtered.length}</span> 部作品
          </p>
        </div>
        <div className="inline-flex items-center p-1 rounded-full bg-background-100 border border-background-200 self-start">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => changeTab(item.key)}
              className={`px-3.5 h-8 rounded-full text-sm whitespace-nowrap transition-colors ${
                tab === item.key
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-300"></i>
          <p className="mt-3 text-sm text-foreground-600">暂无内容</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {visible.map((item) => (
            <MediaCard key={item.id} media={item} />
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-2">
        <p className="text-xs text-foreground-500">
          第 {currentPage} / {totalPages} 页 · 当前频道：
          {tabs.find((t) => t.key === tab)?.label ?? typeLabel.movie}
        </p>
        <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
      </div>
    </section>
  );
}